import {
  EntityModel,
  EntityStatusModel,
  SPEDimensionBaseModel,
  SupplierBaseModel,
  SupplierLookupModel,
  UserModel,
} from '@abb-procure/data';
import { BooleanInput } from '@angular/cdk/coercion';
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import {
  Component,
  DestroyRef,
  ElementRef,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  numberAttribute,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Subscription, filter, map, pairwise, throttleTime } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { FormRowDirective } from '../../directives/form-row.directive';
import { ContainsPipe } from '../../pipes/contains.pipe';
import { Avatar } from '../avatar/avatar.component';
import { ButtonGroup } from '../button-group/button-group.component';
import { Button } from '../button/button.component';
import { Checkbox } from '../checkbox/checkbox.component';
import { EntityBadge } from '../entity-badge/entity-badge.component';
import { FilterItem } from '../filter-item/filter-item.component';
import { InputSearch } from '../input-search/input-search.component';
import { LoadingIndicator } from '../loading-indicator/loading-indicator.component';
import { StatusBadge } from '../status-badge/status-badge.component';

type SelectionItem = {
  id: string | number;
  getDisplay(): string;
};

type FilterItems =
  | SupplierLookupModel
  | SupplierBaseModel
  | SelectionItem
  | UserModel
  | EntityStatusModel
  | EntityModel
  | SPEDimensionBaseModel
  | string;

type FilterButtonAlignment = 'left' | 'right' | 'auto';

@Component({
  selector: 'procure-filter-button',
  templateUrl: './filter-button.component.html',
  styleUrl: './filter-button.component.scss',
  imports: [
    ReactiveFormsModule,
    InputSearch,
    Button,
    Checkbox,
    FormRowDirective,
    ButtonGroup,
    MatIcon,
    ScrollingModule,
    ContainsPipe,
    Avatar,
    StatusBadge,
    MatTooltip,
    EntityBadge,
    LoadingIndicator,
  ],
  providers: [{ provide: FilterItem, useExisting: FilterButton }],
})
export class FilterButton<T extends FilterItems> extends FilterItem {
  readonly container =
    viewChild.required<ElementRef<HTMLDivElement>>('container');

  readonly scroll = viewChild<CdkVirtualScrollViewport>('scrollViewport');

  readonly overlay = viewChild<ElementRef>('overlay');

  readonly control = input.required<FormControl<T[]>>();
  readonly label = input.required<string>();
  readonly items = input.required<T[] | undefined | null>();

  readonly isLoading = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly itemSize = input<number, number | string>(30, {
    transform: numberAttribute,
  });

  readonly infiniteScroll = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly tooltip = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly primary = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly preferredAlignment = input<FilterButtonAlignment>('auto');

  readonly fetchMore = output<string>();

  protected readonly activeColor = computed(() =>
    this.primary() ? 'primary' : 'black',
  );

  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected readonly controlValue = signal<T[]>([]);

  protected readonly firstValue = computed(() => {
    const value = this.controlValue();

    if (Array.isArray(value)) {
      return value[0];
    }

    return value;
  });

  protected readonly remainder = computed(() => {
    const value = this.controlValue();

    if (Array.isArray(value)) {
      return Math.max(value.length - 1, 0);
    }

    return 0;
  });

  protected readonly tooltipValue = computed(() => {
    const value = this.controlValue();
    const items = Math.max(value.length, 0);

    return items + (items !== 1 ? ' items selected.' : ' item selected.');
  });

  protected readonly availableItems = computed(() => this.items() ?? []);

  protected readonly filteredItems = computed(() => {
    const searchValue = this.searchValue().toLowerCase();

    return this.availableItems().filter((item) => {
      if (this.isEntity(item)) {
        return (
          item.name?.toLowerCase().includes(searchValue) ||
          item.entityNumber?.toLowerCase().includes(searchValue)
        );
      }
      return this.getDisplay(item).toLowerCase().includes(searchValue);
    });
  });

  protected readonly disaplyedItems = computed(() =>
    this.infiniteScroll() ? this.availableItems() : this.filteredItems(),
  );

  protected readonly isOpened = signal(false);
  protected readonly id = uuid();

  protected readonly selectedIds = computed(() =>
    this.controlValue().map((item) => this.getId(item)),
  );

  protected readonly autoAlignment = signal<'left' | 'right'>('left');
  protected readonly alignment = computed<'left' | 'right'>(() => {
    const preferredAlignment = this.preferredAlignment();

    return preferredAlignment === 'auto'
      ? this.autoAlignment()
      : preferredAlignment;
  });

  private readonly searchValue = toSignal(this.searchControl.valueChanges, {
    initialValue: this.searchControl.value,
  });

  private valueSubscription?: Subscription;
  private ignoreClick: boolean = true;
  private scrollInit: boolean = false;
  private readonly destroyRef = inject(DestroyRef);

  // eslint-disable-next-line max-lines-per-function
  constructor() {
    super();

    this.onGlobalClick = this.onGlobalClick.bind(this);

    effect(() => {
      const scroll = this.scroll();

      if (!scroll || this.scrollInit || !this.infiniteScroll()) {
        return;
      }

      this.scrollInit = true;

      scroll
        .elementScrolled()
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(() => scroll?.measureScrollOffset('bottom')),
          pairwise(),
          filter(([y1, y2]) => y2 < y1 && y2 < 400),
          throttleTime(200),
        )
        .subscribe(() => {
          if (this.isLoading()) {
            return;
          }

          this.fetchMore.emit(this.searchControl.value);
        });
    });

    effect(() => {
      if (!this.infiniteScroll()) {
        return;
      }

      this.fetchMore.emit(this.searchValue());
    });

    effect(() => {
      const control = this.control();

      this.valueSubscription?.unsubscribe();

      this.controlValue.set(control.value);

      this.valueSubscription = control.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          this.controlValue.set(value);
        });
    });

    effect((cleanup) => {
      if (this.isOpened()) {
        if (this.preferredAlignment() === 'auto') {
          this.checkPosition();
        }
        document.addEventListener('click', this.onGlobalClick);
      } else {
        document.removeEventListener('click', this.onGlobalClick);
      }

      cleanup(() => {
        document.removeEventListener('click', this.onGlobalClick);
      });
    });
  }

  protected toggleOverlay(): void {
    this.isOpened.update((value) => !value);
  }

  protected reset(): void {
    this.control().setValue([]);
  }

  protected selectAll(): void {
    this.control().setValue(this.filteredItems());
  }

  protected getId(item: T): string {
    return typeof item === 'string' ? item : `${item.id}`;
  }

  protected getDisplay(item: T): string {
    return typeof item === 'string' ? item : item.getDisplay();
  }

  protected isUser(item: FilterItem): item is UserModel {
    return item instanceof UserModel;
  }

  protected isStatus(item: FilterItem): item is EntityStatusModel {
    return item instanceof EntityStatusModel;
  }

  protected isEntity(item: FilterItem): item is EntityModel {
    return item instanceof EntityModel;
  }

  protected toggleSelection(event: MatCheckboxChange, item: T): void {
    if (event.checked) {
      this.control().setValue([...this.control().value, item]);
    } else {
      this.control().setValue(
        this.control().value.filter((i) => this.getId(i) !== this.getId(item)),
      );
    }
  }

  private onGlobalClick(event: MouseEvent): void {
    if (this.ignoreClick) {
      this.ignoreClick = false;
      return;
    }

    let element = event.target as HTMLElement;

    while (element && element.id !== this.id) {
      element = element.parentElement as HTMLElement;
    }

    if (element) {
      return;
    }

    this.isOpened.set(false);
    document.removeEventListener('click', this.onGlobalClick);
  }

  private checkPosition(): void {
    const { nativeElement } = this.container();

    const { left } = nativeElement.getBoundingClientRect();
    const width = Math.max(nativeElement.offsetWidth, 320);

    this.autoAlignment.set(
      left + width > document.body.offsetWidth ? 'right' : 'left',
    );
  }
}
